/**
 * =============================================================================
 * IDS CRM Web - Unfinished Log Work on a job (Phase 6 step 7, spec 6.4)
 * =============================================================================
 *
 * A job's saved Log Work is shared by the crew and is NOT hours: only Save
 * Work Log files hours. This file holds what the job page shows about it:
 *
 *  - SavedLogWorkPanel: the Work Logs tab's "Unfinished Log Work (not hours
 *    yet)" card. One line per part (who filled in each robot, the notes, the
 *    crew...), one line for all its Parts used (spare parts, Push 2),
 *    answers kept for reference, and every Job Stages time on the
 *    job that is in no hours yet, with [Continue] and, for a team leader,
 *    [Clear...].
 *  - ClearLogWorkModal: a team leader clears it with a reason. The Job
 *    Stages times in no hours are listed first, each with the way to get
 *    them paid (add the engineer to the crew of that day's log) or why that
 *    cannot be done. unfiled_seen is sent, so the server refuses the clear
 *    when new times arrived since the window opened.
 *  - Small wording helpers shared with the Log Work window and Confirm my
 *    hours: UK days as "Mon 21 Sep" (written out by hand: en-GB Intl prints
 *    September as "Sept"), times as "10:42" today or "Tue 22 Sep 10:42".
 *
 * Everything comes from useSavedLogWork (the GET /tasks/:id/log-work view);
 * nothing here writes except the clear.
 * =============================================================================
 */

import React, { useState } from 'react';
import { formatTime } from '../utils/format';
import { ukTodayKey } from '../utils/dates';
import { shortDayLabel } from '../utils/returnVisits';
import { logWorkPartLabel } from '../hooks/useSavedLogWork';
import { quantityText, spareLineLabel } from '../utils/spareLines';
import './SavedLogWorkPanel.css';

// ---------------------------------------------------------------------------
// Wording helpers
// ---------------------------------------------------------------------------

const validDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const lower = (value) => String(value ?? '').trim().toLowerCase();

/** The UK day (YYYY-MM-DD) an instant falls on, or null. */
export function ukDayKeyOf(value) {
  const date = validDate(value);
  return date ? ukTodayKey(date) : null;
}

/** 'Mon' (or 'Monday' with style 'long') for a UK day key. */
export function ukWeekdayOfKey(key, style = 'short') {
  if (!key) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', { weekday: style, timeZone: 'Europe/London' })
      .format(new Date(`${key}T12:00:00Z`));
  } catch (error) {
    return '';
  }
}

/** 'Mon 21 Sep' for a UK day key. */
export function ukDayLabelOfKey(key) {
  if (!key) return '';
  const weekday = ukWeekdayOfKey(key);
  return `${weekday ? `${weekday} ` : ''}${shortDayLabel(key)}`;
}

/** 'Mon 21 Sep' for an instant, in UK time. */
export function ukDayLabelOf(value) {
  return ukDayLabelOfKey(ukDayKeyOf(value));
}

/** '10:42' on today's UK day, otherwise 'Tue 22 Sep 10:42'. '' when there is no time. */
export function whenText(value, now = new Date()) {
  const date = validDate(value);
  if (!date) return '';
  const key = ukTodayKey(date);
  const clock = formatTime(date);
  return key === ukTodayKey(now) ? clock : `${ukDayLabelOfKey(key)} ${clock}`;
}

/** 'A', 'A and B', 'A, B and C'. */
export function listJoin(items) {
  const list = (items || []).filter(Boolean);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

export const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || 'them';

const capitalise = (text) => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text);

/** Job Stages columns in order, as the card names them. */
export const JOURNEY_STAMPS = [
  ['departed_at', 'Job started'],
  ['arrived_at', 'Arrived at site'],
  ['departed_site_at', 'Finished site work'],
  ['returned_at', 'Arrived home / changed job']
];

/**
 * 'Job started 07:50, Arrived at site 09:10' - the first stamp with its day
 * when it is not today, the later ones with their day only when it moved on.
 */
export function journeyStampsText(journey, now = new Date()) {
  if (!journey) return '';
  let dayShown = null;
  const phrases = [];
  JOURNEY_STAMPS.forEach(([column, label]) => {
    const value = journey[column];
    const date = validDate(value);
    if (!date) return;
    const key = ukTodayKey(date);
    const clock = formatTime(date);
    let text;
    if (dayShown === null) text = whenText(date, now);
    else text = key === dayShown ? clock : whenText(date, now);
    dayShown = key;
    phrases.push(`${label} ${text}`);
  });
  return phrases.join(', ');
}

/** The first and last Job Stages time of a journey ({ start, end, endLabel }). */
export function journeySpan(journey) {
  if (!journey) return { start: null, end: null, endLabel: null };
  const start = validDate(journey.departed_at);
  let end = null;
  let endLabel = null;
  JOURNEY_STAMPS.slice(1).forEach(([column, label]) => {
    const date = validDate(journey[column]);
    if (date) {
      end = date;
      endLabel = label;
    }
  });
  return { start, end, endLabel };
}

/**
 * The line under Continue Log Work: 'Saved 10:42 by Mike Smith · 2 of 4
 * robots done · carried on from Mon 21 Sep'. Changes only on this phone read
 * 'Changes on this phone not sent yet'.
 */
export function continueSubtitle(saved, now = new Date()) {
  if (!saved) return '';
  const draft = saved.draft || null;
  const bits = [];
  if (draft && draft.has_content && draft.last_changed_at) {
    const when = whenText(draft.last_changed_at, now);
    bits.push(`Saved ${when}${draft.last_changed_by_name ? ` by ${draft.last_changed_by_name}` : ''}`);
  } else {
    bits.push('Changes on this phone not sent yet');
  }
  const robots = saved.robots || (draft && draft.robots) || null;
  if (draft && robots && robots.total > 0) bits.push(`${robots.done} of ${robots.total} robots done`);
  const carried = (draft && draft.carried_from) || saved.carriedFrom || null;
  if (carried && carried.saved_at) bits.push(`carried on from ${ukDayLabelOf(carried.saved_at)}`);
  return bits.join(' · ');
}

const isArchiveKey = (key) => String(key).startsWith('archive:');
const isRobotKey = (key) => String(key).startsWith('robot:');
const isFormKey = (key) => isRobotKey(key) || key === 'form:task';
const isSpareKey = (key) => String(key).startsWith('spare:');

const timeOrZero = (value) => {
  const date = validDate(value);
  return date ? date.getTime() : 0;
};

/**
 * Parts used (spare parts, Push 2): one line for all of them, 'Parts used: 4
 * lines (Brush Driver Shaft (PTL-11537A) ×2.5, Nozzle (NZ-200) ×1, Resolver
 * (RES-100) ×1 and 1 more)', in the order they were added. Each part carries
 * its Item Number when it has one, so two parts with the same name can be
 * told apart. '' when there are none.
 */
export function sparePartsSummary(parts) {
  const lines = Object.entries(parts || {})
    .filter(([key, part]) => isSpareKey(key) && part && part.value)
    .map(([key, part]) => ({ key, value: part.value }))
    .sort((a, b) => (timeOrZero(a.value.added_at) - timeOrZero(b.value.added_at)) || a.key.localeCompare(b.key));
  if (lines.length === 0) return '';
  const n = lines.length;
  const shown = lines.slice(0, 3).map(line => `${spareLineLabel(line.value.name, line.value.item_number)} ×${quantityText(line.value.quantity)}`).join(', ');
  return `Parts used: ${n} ${n === 1 ? 'line' : 'lines'} (${shown}${n > 3 ? ` and ${n - 3} more` : ''})`;
}

/** What a part is called in a line: 'BR-002', 'The notes', 'The crew'. */
export function partLineLabel(partKey, assetRefs) {
  return capitalise(logWorkPartLabel(partKey, assetRefs || {}));
}

const OUTCOME_WORDS = { done: 'Done', not_done: 'Not done' };

function partSummary(partKey, part, assetRefs, now) {
  const label = partLineLabel(partKey, assetRefs);
  const who = part.changed_by_name || 'Someone';
  const when = whenText(part.changed_at, now);
  const verb = isFormKey(partKey) ? 'filled in by' : 'changed by';
  const bits = [`${label} - ${verb} ${who}${when ? `, ${when}` : ''}`];
  const value = part.value || {};
  if (isRobotKey(partKey) && OUTCOME_WORDS[value.outcome]) {
    const formDone = value.form && value.form.completed === true;
    bits.push(value.outcome === 'done' ? `${OUTCOME_WORDS.done}${formDone ? ', check form complete' : ''}` : OUTCOME_WORDS.not_done);
  }
  if (part.carried) bits.push('carried on from the last visit');
  return bits.join(' · ');
}

function archiveSummary(part, assetRefs, now) {
  const value = part.value || {};
  const label = partLineLabel(value.part_key || 'form:task', assetRefs);
  const who = part.changed_by_name || 'someone';
  const when = whenText(value.archived_at || part.changed_at, now);
  return `${label} - answers by ${who}${when ? ` (set aside ${when})` : ''}${value.why ? `, kept because the ${value.why}` : ''}`;
}

/**
 * Every Job Stages record to show: the job's unfiled ones (in no hours) and
 * the journeys saved in the Log Work, each once.
 */
export function journeysToShow(saved) {
  const rows = [];
  const seen = new Set();
  (Array.isArray(saved && saved.unfiled) ? saved.unfiled : []).forEach(row => {
    const id = lower(row.travel_id);
    if (seen.has(id)) return;
    seen.add(id);
    rows.push({ ...row, user_id: row.engineer_id, unfiled: true });
  });
  (Array.isArray(saved && saved.journeys) ? saved.journeys : []).forEach(row => {
    const id = lower(row.travel_id);
    if (!id || seen.has(id)) return;
    seen.add(id);
    rows.push({ ...row, engineer_id: row.user_id, unfiled: false });
  });
  return rows;
}

// ---------------------------------------------------------------------------
// The Work Logs tab card
// ---------------------------------------------------------------------------

/**
 * @param {object}   props
 * @param {object}   props.saved        useSavedLogWork's view.
 * @param {object}   [props.assetRefs]  { asset_id: 'BR-002' }.
 * @param {boolean}  [props.canContinue] Show [Continue].
 * @param {boolean}  [props.canClear]    Show [Clear...] (the server's can_clear and the viewer's role).
 * @param {Function} [props.onContinue]
 * @param {Function} [props.onClear]
 * @param {Date}     [props.now]         Tests: the time 'today' is read against.
 */
export function SavedLogWorkPanel({ saved, assetRefs = {}, canContinue = false, canClear = false, onContinue, onClear, now: nowProp = null }) {
  if (!saved || (saved.status !== 'ready' && saved.status !== 'offline')) return null;
  const now = nowProp || new Date();
  const draft = saved.draft || null;
  const parts = saved.parts || {};
  const journeys = journeysToShow(saved);
  const hasContent = Boolean(saved.hasContent);
  if (!hasContent && journeys.length === 0) return null;

  // Parts used lines are summed up in one line of their own, below.
  const partKeys = Object.keys(parts)
    .filter(key => !isArchiveKey(key) && !key.startsWith('journey:') && !isSpareKey(key))
    .filter(key => parts[key] && parts[key].value !== null && parts[key].value !== undefined)
    .sort((a, b) => (isFormKey(a) === isFormKey(b) ? a.localeCompare(b) : (isFormKey(a) ? 1 : -1)));
  const spareLine = sparePartsSummary(parts);
  const archives = Object.keys(parts)
    .filter(isArchiveKey)
    .filter(key => parts[key] && parts[key].value)
    .sort();
  const fileCount = Array.isArray(saved.files) ? saved.files.length : 0;

  const heading = hasContent
    ? ['Unfinished Log Work (not hours yet)',
      draft && draft.last_changed_at
        ? `saved ${whenText(draft.last_changed_at, now)}${draft.last_changed_by_name ? ` by ${draft.last_changed_by_name}` : ''}`
        : 'changes on this phone not sent yet',
      ...(draft && draft.robots && draft.robots.total > 0 ? [`${draft.robots.done} of ${draft.robots.total} robots done`] : [])
    ].join(' · ')
    : 'Job Stages not in any hours yet';

  return (
    <section className="saved-log-work-panel" aria-label="Unfinished Log Work">
      <div className="saved-log-work-heading">{heading}</div>
      {draft && draft.carried_from && draft.carried_from.saved_at && (
        <div className="saved-log-work-line muted">
          Carried on from {ukDayLabelOf(draft.carried_from.saved_at)}
          {draft.carried_from.saved_by_name ? `, saved by ${draft.carried_from.saved_by_name}` : ''}.
        </div>
      )}

      {(partKeys.length > 0 || spareLine) && (
        <ul className="saved-log-work-lines" aria-label="What is in it">
          {partKeys.map(key => (
            <li key={key} className="saved-log-work-line">{partSummary(key, parts[key], assetRefs, now)}</li>
          ))}
          {spareLine && <li className="saved-log-work-line">{spareLine}</li>}
          {fileCount > 0 && (
            <li className="saved-log-work-line">{fileCount === 1 ? '1 photo or file' : `${fileCount} photos and files`}</li>
          )}
        </ul>
      )}

      {journeys.length > 0 && (
        <div className="saved-log-work-block">
          <div className="saved-log-work-subheading">Job Stages</div>
          <ul className="saved-log-work-lines" aria-label="Job Stages">
            {journeys.map(row => (
              <li key={row.travel_id} className="saved-log-work-line">
                {row.full_name || 'Someone'}: {journeyStampsText(row, now) || 'no times yet'}
                {row.unfiled ? ' - not in any hours' : ' - in this Log Work'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {archives.length > 0 && (
        <div className="saved-log-work-block">
          <div className="saved-log-work-subheading">Kept for reference</div>
          <ul className="saved-log-work-lines" aria-label="Kept for reference">
            {archives.map(key => (
              <li key={key} className="saved-log-work-line muted">{archiveSummary(parts[key], assetRefs, now)}</li>
            ))}
          </ul>
        </div>
      )}

      {(canContinue || canClear) && (
        <div className="saved-log-work-actions">
          {canContinue && (
            <button type="button" className="btn-small" onClick={onContinue}>Continue</button>
          )}
          {canClear && (
            <button type="button" className="btn-small secondary" onClick={onClear}>Clear...</button>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The clear window (team leaders and up)
// ---------------------------------------------------------------------------

const workPeriodLabel = (config, value) => {
  const found = (config && Array.isArray(config.work_periods) ? config.work_periods : [])
    .find(period => period.value === value);
  const label = found ? found.label : String(value || '').replace(/_/g, ' ');
  return label ? label.charAt(0).toLowerCase() + label.slice(1) : '';
};

/**
 * How one unfiled journey can still be paid: the same-day lead log it can be
 * added to (not payroll-locked, not the engineer's own), or why not.
 */
export function crewOptionFor(journey, leadLogs) {
  const dayKey = ukDayKeyOf(journey && journey.departed_at);
  const sameDay = (Array.isArray(leadLogs) ? leadLogs : [])
    .filter(log => log && log.uk_day === dayKey && lower(log.engineer_id) !== lower(journey && journey.engineer_id));
  const usable = sameDay.find(log => !log.payroll_locked) || null;
  if (usable) return { dayKey, log: usable, locked: null };
  return { dayKey, log: null, locked: sameDay[0] || null };
}

/** 'Hours from unfinished Log Work: Job started Tue 22 Sep 07:50 to Arrived home Tue 22 Sep 17:40'. */
export function crewReasonFor(journey) {
  const { start, end, endLabel } = journeySpan(journey);
  const from = start ? `Job started ${ukDayLabelOf(start)} ${formatTime(start)}` : 'Job started';
  const to = end ? ` to ${endLabel} ${ukDayLabelOf(end)} ${formatTime(end)}` : '';
  return `Hours from unfinished Log Work: ${from}${to}`;
}

/**
 * @param {object}   props
 * @param {string}   props.taskId
 * @param {object}   props.task
 * @param {object}   props.saved       useSavedLogWork's view.
 * @param {object}   props.config      Work log config (work period labels).
 * @param {Function} props.authFetch
 * @param {Function} props.onClose
 * @param {Function} props.onCleared
 * @param {Function} [props.onAddToCrew] (leadLog, journey, reason) => void: open Change crew on that log.
 */
export function ClearLogWorkModal({ taskId, task, saved, config, authFetch, onClose, onCleared, onAddToCrew }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const draftId = saved && saved.draftId;
  const unfiled = Array.isArray(saved && saved.unfiled) ? saved.unfiled : [];
  const leadLogs = Array.isArray(saved && saved.leadLogs) ? saved.leadLogs : [];
  const jobRef = (task && task.task_ref) || 'this job';
  const reasonOk = reason.trim().length >= 5;
  const now = new Date();

  const submit = async () => {
    if (!draftId) {
      setError('There is no unfinished Log Work to clear on this job.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/tasks/${taskId}/log-work/${draftId}/clear`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim(), unfiled_seen: unfiled.map(row => row.travel_id) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409 && saved && typeof saved.refresh === 'function') saved.refresh();
        throw new Error(data.message || 'The unfinished Log Work could not be cleared. Try again.');
      }
      onCleared(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content clear-log-work-modal" onClick={event => event.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <h2>Clear unfinished Log Work</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error" role="alert">{error}</div>}
          <p className="modal-hint">
            Nothing in this Log Work has been saved as hours. Clearing it keeps what was written for reference,
            and everyone who worked on it is told why.
          </p>

          {unfiled.length > 0 ? (
            <div className="saved-log-work-block">
              <div className="saved-log-work-subheading">Job Stages not in any hours</div>
              <ul className="clear-log-work-journeys" aria-label="Job Stages not in any hours">
                {unfiled.map(row => {
                  const name = row.full_name || 'This engineer';
                  const first = firstNameOf(name);
                  const option = crewOptionFor(row, leadLogs);
                  const dayLabel = ukDayLabelOfKey(option.dayKey);
                  return (
                    <li key={row.travel_id} className="clear-log-work-journey">
                      <div className="saved-log-work-line">{name}: {journeyStampsText(row, now)}</div>
                      {option.log ? (
                        <>
                          <button
                            type="button"
                            className="btn-small"
                            onClick={() => onAddToCrew && onAddToCrew(option.log, row, crewReasonFor(row))}
                          >
                            Add {name} to {option.log.engineer_name || 'the lead'}&apos;s log for {dayLabel}
                            {option.log.work_period ? ` (${workPeriodLabel(config, option.log.work_period)})` : ''}
                          </button>
                          <div className="field-help">
                            {first}&apos;s entry takes that log&apos;s times and work period; {first} corrects the times with Confirm my hours.
                          </div>
                        </>
                      ) : (
                        <div className="field-help">
                          {option.locked
                            ? `${option.locked.engineer_name || 'The lead'}'s log for ${dayLabel} is in a finalised pay month.`
                            : `No log on this job is dated ${dayLabel}.`}
                          {' '}Ask {name} to open {jobRef} and save Log Work while the job is open.
                          {' '}If you clear it, these times will not be paid unless {first} is added to a crew.
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="saved-log-work-line">No Job Stages were left unfiled.</p>
          )}

          <div className="form-group">
            <label htmlFor="clear_log_work_reason">Why is it being cleared? *</label>
            <textarea
              id="clear_log_work_reason"
              rows={3}
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="e.g. Visit abandoned - Mike's notes are on the follow-up ticket"
            />
            <span className="field-help">At least 5 characters. Everyone who worked on it is told.</span>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>Keep it</button>
          <button type="button" className="btn-danger" onClick={submit} disabled={saving || !reasonOk}>
            {saving ? 'Clearing...' : 'Clear Log Work'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SavedLogWorkPanel;
